/**
 * Fetches the MCP tools available to the current org.
 *
 * Backed by GET /api/orgs/<orgId>/mcp/tools/ — a browser-facing, read-only endpoint
 * that returns exactly the tools this org is entitled to call (entitlement-filtered
 * server-side). Used by the MCP settings page to document the tool surface dynamically
 * rather than maintaining a hand-written list that drifts from what clients actually see.
 */

import { useQuery } from '@tanstack/react-query';

import { apiGet } from '@/lib/http/client';

/**
 * A (subset of a) JSON Schema property, as generated by pydantic on the
 * backend. Optional fields arrive as `anyOf: [{type: X}, {type: 'null'}]`
 * with no top-level `type`, arrays carry their element schema in `items`,
 * closed value sets arrive as `enum`, and nested models are `$ref`
 * pointers into the schema's `$defs` (whose entries reuse this same
 * shape — an enum def or an object def with `properties`/`required`).
 */
export interface McpToolSchemaProperty {
  type?: string;
  description?: string;
  anyOf?: McpToolSchemaProperty[];
  items?: McpToolSchemaProperty;
  $ref?: string;
  enum?: (string | number)[];
  properties?: Record<string, McpToolSchemaProperty>;
  required?: string[];
}

export interface McpToolInputSchema {
  type?: string;
  properties?: Record<string, McpToolSchemaProperty>;
  required?: string[];
  $defs?: Record<string, McpToolSchemaProperty>;
}

export interface McpTool {
  name: string;
  description: string;
  input_schema: McpToolInputSchema;
}

interface McpToolListResponse {
  tools: McpTool[];
}

export function useMcpTools(orgId: number | undefined) {
  return useQuery({
    queryKey: ['orgs', orgId, 'mcp', 'tools'],
    queryFn: () => apiGet<McpToolListResponse>(`/api/orgs/${orgId}/mcp/tools/`),
    enabled: orgId !== undefined,
    select: data => data.tools,
  });
}
